import React from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Stack,
  Badge,
  Icon,
  HStack,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { FaArrowLeft, FaBuilding, FaCalendar, FaMapMarkerAlt } from 'react-icons/fa';

const PlanningDetail = ({ application, onBack, formatCategoryName }) => {
  if (!application) return null;

  // Get all applications in the same category
  const categoryApplications = application.categoryApplications || [application];

  // Debug the categoryApplications
  console.log('PlanningDetail - application:', application);
  console.log('PlanningDetail - categoryApplications:', categoryApplications);

  return (
    <Card variant='outline' bg='red.50' width='100%'>
      <CardHeader>
        <HStack spacing={4} mb={4}>
          <Button leftIcon={<FaArrowLeft />} onClick={onBack} variant='outline' size='sm'>
            Back to List
          </Button>
          <Badge
            colorScheme={
              application.decision?.toLowerCase().includes('grant') ||
              application.decision?.toLowerCase().includes('no objection') ||
              application.status?.toLowerCase() === 'granted' ||
              application.status?.toLowerCase() === 'no objection'
                ? 'green'
                : application.decision?.toLowerCase().includes('refuse') ||
                  application.status?.toLowerCase() === 'refused'
                ? 'red'
                : 'gray'
            }
            fontSize='sm'
          >
            {application.decision || 'Pending'}
          </Badge>
        </HStack>
        <HStack spacing={2}>
          <Icon as={FaBuilding} boxSize={6} color='brand.primary' />
          <Heading size='md' color='brand.text'>
            {formatCategoryName ? formatCategoryName(application.category) : application.category}
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <Tabs variant='enclosed' colorScheme='blue' mb={4}>
          <TabList>
            <Tab>Selected Application</Tab>
            <Tab>
              All{' '}
              {formatCategoryName ? formatCategoryName(application.category) : application.category}{' '}
              Applications ({categoryApplications.length})
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0} pt={4}>
              <Stack spacing={4}>
                <Box>
                  <Text fontWeight='bold' mb={1}>
                    Description
                  </Text>
                  <Text>{application.proposal}</Text>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight='bold' mb={1}>
                    Details
                  </Text>
                  <Stack spacing={2}>
                    <HStack>
                      <Icon as={FaCalendar} color='brand.primary' />
                      <Text fontWeight='medium'>Date Submitted:</Text>
                      <Text>
                        {new Date(
                          application.application_date || application.date,
                        ).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Text>
                    </HStack>

                    {(application.location_details || application.address) && (
                      <HStack>
                        <Icon as={FaMapMarkerAlt} color='brand.primary' />
                        <Text fontWeight='medium'>Address:</Text>
                        <Text>{application.location_details || application.address}</Text>
                      </HStack>
                    )}

                    {(application.application_reference || application.reference) && (
                      <HStack>
                        <Text fontWeight='medium'>Reference:</Text>
                        <Text>{application.application_reference || application.reference}</Text>
                      </HStack>
                    )}

                    {(application.application_type || application.type) && (
                      <HStack>
                        <Text fontWeight='medium'>Application Type:</Text>
                        <Text>{application.application_type || application.type}</Text>
                      </HStack>
                    )}

                    {application.area && (
                      <HStack>
                        <Text fontWeight='medium'>Area:</Text>
                        <Text>{application.area}</Text>
                      </HStack>
                    )}

                    {application.postcode && (
                      <HStack>
                        <Text fontWeight='medium'>Postcode:</Text>
                        <Text>{application.postcode}</Text>
                      </HStack>
                    )}

                    {(application.link || application.url) && (
                      <HStack>
                        <Text fontWeight='medium'>Link:</Text>
                        <Text
                          as='a'
                          href={application.link || application.url}
                          target='_blank'
                          color='blue.500'
                          textDecoration='underline'
                        >
                          View Original Application
                        </Text>
                      </HStack>
                    )}
                  </Stack>
                </Box>

                {application.applicant && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight='bold' mb={1}>
                        Applicant
                      </Text>
                      <Text>{application.applicant}</Text>
                    </Box>
                  </>
                )}

                <Divider />
                <Box>
                  <Text fontWeight='bold' mb={1}>
                    Technical Details
                  </Text>
                  <Stack spacing={2}>
                    {application.id && (
                      <HStack>
                        <Text fontWeight='medium'>ID:</Text>
                        <Text>{application.id}</Text>
                      </HStack>
                    )}

                    {application.latitude && application.longitude && (
                      <HStack>
                        <Text fontWeight='medium'>Coordinates:</Text>
                        <Text>
                          {application.latitude}, {application.longitude}
                        </Text>
                      </HStack>
                    )}

                    {application.distance !== undefined &&
                      typeof application.distance === 'number' && (
                        <HStack>
                          <Text fontWeight='medium'>Distance:</Text>
                          <Text>{application.distance.toFixed(3)} km</Text>
                        </HStack>
                      )}

                    {application.category_confidence !== undefined &&
                      application.category_confidence !== null && (
                        <HStack>
                          <Text fontWeight='medium'>Category Confidence:</Text>
                          <Text>{application.category_confidence}</Text>
                        </HStack>
                      )}
                  </Stack>
                </Box>
              </Stack>
            </TabPanel>

            <TabPanel p={0} pt={4}>
              <Stack spacing={4}>
                {categoryApplications.map((app, index) => (
                  <Box
                    key={index}
                    p={4}
                    borderWidth='1px'
                    borderRadius='lg'
                    bg={app.id === application.id ? 'blue.50' : 'white'}
                  >
                    <Stack spacing={2}>
                      <Text fontWeight='bold' fontSize='md'>
                        {app.location_details || 'No address available'}
                      </Text>
                      <Text fontSize='sm'>{app.proposal || 'No proposal details'}</Text>
                      <HStack justifyContent='space-between'>
                        <Badge
                          colorScheme={
                            app.decision?.toLowerCase().includes('grant') ||
                            app.decision?.toLowerCase().includes('no objection') ||
                            app.status?.toLowerCase() === 'granted' ||
                            app.status?.toLowerCase() === 'no objection'
                              ? 'green'
                              : app.decision?.toLowerCase().includes('refuse') ||
                                app.status?.toLowerCase() === 'refused'
                              ? 'red'
                              : 'gray'
                          }
                        >
                          {app.decision || app.status || 'Pending'}
                        </Badge>
                        <Text fontSize='xs' color='gray.500'>
                          {new Date(app.application_date || app.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Text>
                      </HStack>
                      {app.reference && (
                        <Text fontSize='xs' color='gray.500'>
                          Ref: {app.reference}
                        </Text>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  );
};

export default PlanningDetail;
